package com.sddamico.draftmachine.pages.pools

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.data.Sport
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.displayBlock
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.displayGrid
import com.sddamico.draftmachine.ds.dmPanel
import com.sddamico.draftmachine.ds.dmStamp
import com.sddamico.draftmachine.ds.forms.Button
import com.sddamico.draftmachine.ds.forms.ButtonVariant
import com.sddamico.draftmachine.ds.forms.InputSize
import com.sddamico.draftmachine.ds.forms.Select
import com.sddamico.draftmachine.ds.overlays.Modal
import com.sddamico.draftmachine.ds.overlays.ModalSize
import com.sddamico.draftmachine.ds.roleH3
import com.sddamico.draftmachine.models.NewPlayerEntry
import com.sddamico.draftmachine.util.sportLabel
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Text

// ── Pure CSV parsing ─────────────────────────────────────────────────────────

/** What a CSV column can map to. */
enum class CsvField(val label: String) {
  Name("Player name"), Sport("Sport"), Team("Team"), Position("Position"),
  Adp("ADP"), AdpPpr("ADP (PPR)"), Ignore("Ignore")
}

data class ParsedCsv(val headers: List<String>, val rows: List<List<String>>, val badLines: Int)

/** Result of applying a column mapping: entries ready to add + the dedupe/skip report. */
data class CsvImportReport(
  val entries: List<NewPlayerEntry>,
  val duplicatesSkipped: Int,
  val unreadable: Int,
)

/** Split one CSV line honoring double quotes; null on an unterminated quote. */
private fun splitCsvLine(line: String): List<String>? {
  val fields = mutableListOf<String>()
  val current = StringBuilder()
  var inQuotes = false
  var i = 0
  while (i < line.length) {
    val c = line[i]
    when {
      inQuotes && c == '"' && i + 1 < line.length && line[i + 1] == '"' -> { current.append('"'); i++ }
      c == '"' -> inQuotes = !inQuotes
      c == ',' && !inQuotes -> { fields += current.toString(); current.clear() }
      else -> current.append(c)
    }
    i++
  }
  if (inQuotes) return null
  fields += current.toString()
  return fields
}

/** Parse the file into a header row + data rows (cells untrimmed beyond edges). */
fun parsePoolCsv(text: String): ParsedCsv? {
  val lines = text.split("\r\n", "\r", "\n").filter { it.isNotBlank() }
  if (lines.isEmpty()) return null
  val header = splitCsvLine(lines.first())?.map { it.trim() } ?: return null
  var bad = 0
  val rows = lines.drop(1).mapNotNull { line ->
    splitCsvLine(line)?.map { it.trim() } ?: run { bad++; null }
  }
  return ParsedCsv(header, rows, bad)
}

/** Best-guess field for a CSV header name, for pre-seeding the mapping selects. */
fun guessField(header: String): CsvField {
  val h = header.lowercase().replace(Regex("[^a-z]"), "")
  return when {
    h.contains("name") || h == "player" -> CsvField.Name
    h.contains("sport") || h.contains("league") -> CsvField.Sport
    h.contains("team") || h.contains("club") -> CsvField.Team
    h.contains("pos") -> CsvField.Position
    h.contains("adpppr") || h.contains("ppradp") -> CsvField.AdpPpr
    h.contains("adp") -> CsvField.Adp
    else -> CsvField.Ignore
  }
}

/** Lenient sport parsing for CSV cells ("nfl", "Football", "NCAA Football", …). */
fun parseSportToken(raw: String): Sport? {
  val t = raw.uppercase().replace(Regex("[^A-Z]"), "")
  return when (t) {
    "NFL", "FOOTBALL" -> Sport.NFL
    "MLB", "BASEBALL" -> Sport.MLB
    "NBA", "BASKETBALL" -> Sport.NBA
    "NHL", "HOCKEY" -> Sport.NHL
    "NCAAF", "NCAAFOOTBALL", "CFB", "COLLEGEFOOTBALL" -> Sport.NCAA_FOOTBALL
    "NCAAB", "NCAABASKETBALL", "CBB", "COLLEGEBASKETBALL" -> Sport.NCAA_BASKETBALL
    "GOLF", "PGA", "LPGA" -> Sport.GOLF
    "TENNIS", "ATP", "MENSTENNIS" -> Sport.MENS_TENNIS
    "WTA", "WOMENSTENNIS" -> Sport.WOMENS_TENNIS
    else -> runCatching { Sport.valueOf(raw.trim().uppercase().replace(' ', '_')) }.getOrNull()
  }
}

/**
 * Apply [mapping] (one [CsvField] per column) to the parsed rows. Rows without a readable name —
 * or without any sport for a mixed pool — count as unreadable; entries matching [existingKeys]
 * (lowercased player names already in the pool)
 * or repeated within the file are deduped. A non-null [fallbackSport] is the pool's authoritative
 * sport, so any mapped Sport column is ignored.
 */
fun buildImportReport(
  csv: ParsedCsv,
  mapping: List<CsvField>,
  fallbackSport: Sport?,
  existingKeys: Set<String>,
): CsvImportReport {
  var unreadable = csv.badLines
  var duplicates = 0
  val seen = existingKeys.toMutableSet()
  val entries = mutableListOf<NewPlayerEntry>()

  for (row in csv.rows) {
    fun cell(field: CsvField): String? =
      mapping.withIndex().firstOrNull { it.value == field }?.let { row.getOrNull(it.index) }?.takeIf { it.isNotBlank() }

    val name = cell(CsvField.Name) ?: run { unreadable++; continue }
    val sport = fallbackSport ?: cell(CsvField.Sport)?.let(::parseSportToken)
      ?: run { unreadable++; continue }
    val adpRaw = cell(CsvField.Adp)
    val adpPprRaw = cell(CsvField.AdpPpr)
    val adp = adpRaw?.toDoubleOrNull()?.takeIf { it.isFinite() }
    val adpPpr = adpPprRaw?.toDoubleOrNull()?.takeIf { it.isFinite() }
    if ((adpRaw != null && adp == null) || (adpPprRaw != null && adpPpr == null)) {
      unreadable++
      continue
    }
    val key = name.trim().lowercase()
    if (!seen.add(key)) { duplicates++; continue }
    entries += NewPlayerEntry(
      name = name.trim(),
      sport = sport,
      team = cell(CsvField.Team),
      position = cell(CsvField.Position),
      adp = adp,
      adpPpr = adpPpr,
    )
  }
  return CsvImportReport(entries, duplicates, unreadable)
}

// ── Mapping modal (frame 1f) ─────────────────────────────────────────────────

/**
 * The CSV column-mapping + dedupe-report step. [existingKeys] are lowercased names already in the
 * pool (inline adds + prior imports); [fallbackSport] is inherited by every imported row.
 */
@Composable
fun PoolCsvMappingModal(
  fileName: String,
  csv: ParsedCsv,
  fallbackSport: Sport?,
  existingKeys: Set<String>,
  onImport: (List<NewPlayerEntry>) -> Unit,
  onDismiss: () -> Unit,
) {
  var mapping by remember {
    mutableStateOf(
      csv.headers.map { header ->
        guessField(header).takeUnless { fallbackSport != null && it == CsvField.Sport } ?: CsvField.Ignore
      },
    )
  }
  val report = buildImportReport(csv, mapping, fallbackSport, existingKeys)

  Modal(
    open = true,
    title = fileName,
    icon = "file-csv",
    size = ModalSize.Sm,
    onClose = onDismiss,
    footer = {
      Button("Cancel", variant = ButtonVariant.Ghost, onClick = onDismiss)
      Button(
        "Add ${report.entries.size} players", variant = ButtonVariant.Primary, icon = "plus",
        disabled = report.entries.isEmpty(),
        onClick = { onImport(report.entries) },
      )
    },
  ) {
    Div(
      attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(14.px).toAttrs(),
    ) {
      SpanText(
        "${csv.rows.size} ROWS READ",
        Modifier.fontFamily(*DmType.mono).fontSize(11.px).color(DmColor.textMuted).displayBlock(),
      )

      SpanText("Match the columns", Modifier.dmStamp())
      SpanText(
        if (fallbackSport != null) {
          "${sportLabel(fallbackSport)} comes from the pool. Name is required; Position, Team, and ADP are optional."
        } else {
          "Mixed pools require Name and Sport; Position, Team, and ADP are optional."
        },
        Modifier.fontFamily(*DmType.body).fontSize(DmType.textXs).color(DmColor.textMuted).displayBlock(),
      )
      csv.headers.forEachIndexed { i, header ->
        Div(
          attrs = Modifier.displayGrid().gap(8.px).alignItems(AlignItems.Center)
            .styleModifier { property("grid-template-columns", "1fr 20px 1fr") }.toAttrs()
        ) {
          SpanText(
            header,
            Modifier.fontFamily(*DmType.mono).fontSize(12.px).fontWeight(DmType.weightBold)
              .color(DmColor.textSecondary).padding(topBottom = 8.px, leftRight = 12.px)
              .border(1.px, LineStyle.Solid, DmColor.border).borderRadius(DmEffects.radiusSm)
              .backgroundColor(DmColor.surfaceInset)
              .overflow(com.varabyte.kobweb.compose.css.Overflow.Hidden)
              .styleModifier { property("text-overflow", "ellipsis"); property("white-space", "nowrap") },
          )
          Icon("arrow-right", IconWeight.Bold, Modifier.fontSize(14.px).color(DmColor.brass400))
          Select(
            size = InputSize.Sm,
            value = mapping[i].name,
            onValueChange = { v ->
              val f = runCatching { CsvField.valueOf(v) }.getOrNull() ?: CsvField.Ignore
              mapping = mapping.mapIndexed { j, existing -> if (j == i) f else existing }
            },
          ) {
            CsvField.entries
              .filter { fallbackSport == null || it != CsvField.Sport }
              .forEach { f ->
                Option(
                  value = f.name,
                  attrs = { if (mapping[i] == f) attr("selected", "") },
                ) { Text(f.label) }
              }
          }
        }
      }

      // Dedupe / readiness report.
      val ok = report.entries.isNotEmpty()
      Div(
        attrs = Modifier.displayFlex().alignItems(AlignItems.FlexStart).gap(12.px).padding(14.px)
          .borderRadius(DmEffects.radiusMd)
          .border(1.px, LineStyle.Solid, if (ok) DmColor.verde600 else DmColor.rust500)
          .backgroundColor(if (ok) Color.rgba(74, 155, 109, 0.07f) else Color.rgba(180, 71, 42, 0.08f))
          .toAttrs()
      ) {
        Icon(
          if (ok) "check-circle" else "warning-octagon", IconWeight.Fill,
          Modifier.fontSize(20.px).color(if (ok) DmColor.verde400 else DmColor.rust400).margin(top = 1.px),
        )
        Div {
          SpanText(
            if (ok) "${report.entries.size} ready to add" else "Nothing importable",
            Modifier.roleH3().fontSize(14.px).displayBlock(),
          )
          SpanText(
            "${report.duplicatesSkipped} duplicates already in the pool were skipped · ${report.unreadable} rows unreadable.",
            Modifier.fontFamily(*DmType.body).fontSize(DmType.textXs).color(DmColor.textMuted)
              .displayBlock().margin(top = 2.px),
          )
        }
      }

      if (report.entries.isNotEmpty()) {
        SpanText("Preview", Modifier.dmStamp())
        Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(6.px).toAttrs()) {
          report.entries.take(3).forEach { r ->
            Div(
              attrs = Modifier.displayGrid().gap(8.px)
                .styleModifier { property("grid-template-columns", "1.4fr .8fr .6fr .6fr") }
                .padding(topBottom = 9.px, leftRight = 12.px)
                .border(1.px, LineStyle.Solid, DmColor.border).borderRadius(DmEffects.radiusSm)
                .backgroundColor(DmColor.surfaceInset).toAttrs()
            ) {
              SpanText(r.name, Modifier.fontFamily(*DmType.body).fontSize(13.px).fontWeight(DmType.weightSemibold).color(DmColor.text))
              SpanText(r.sport?.name ?: "—", csvCellMod())
              SpanText(r.team ?: "—", csvCellMod())
              SpanText(r.position ?: "—", csvCellMod())
            }
          }
        }
      }

    }
  }
}

private fun csvCellMod(): Modifier =
  Modifier.fontFamily(*DmType.mono).fontSize(11.px).color(DmColor.textSecondary)
